import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { mergeMap, map, shareReplay, filter, flatMap } from 'rxjs/operators';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/reduce';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/concat';
import 'rxjs/add/observable/forkJoin'
import 'rxjs/operator/shareReplay';

import { environment } from '../../environments/environment';
import { IS1Token } from '../models/IS1Token';
import { IQuota } from '../models/IQuota';
import { IQuestion } from '../models/IQuestion';
import { IQuotaTable } from '../models/IQuotaTable';
import { IS1Quota } from '../models/IS1Quota';
import { Item, IItem } from '../models/IItem';
import { ITextItem } from '../models/ITextItem';
import { IBillingEntity } from '../models/IBillingEntity';
import { ICurrency } from '../models/ICurrency';
import { ISupplier } from '../models/ISupplier';
import { ICategory } from '../models/ICategory';
import { ISampleCategory } from '../models/ISampleCategory';
import { IBid, Bid } from '../models/IBid';
import { BidMode, BidModeList } from '../enums/bid-mode.enum'
import { IS1Question } from '../models/IS1Question'
//import { QAs } from '../models/QAs';
import { ICountry } from '../models/ICountry'
import { IS1Answer } from '../models/IS1Answer';
import { IAnswer } from '../models/IAnswer';
import { ILanguage } from '../models/ILanguage';
import { IS1UserModel } from '../models/IS1UserModel';
import { IS1SubSample } from '../models/IS1SubSample';
import { ISample } from "../models/ISample";
import { IResearchCategory, ResearchCategory } from '../models/IResearchCategory';
import { IS1Event } from '../models/S1Event';
import { Sample } from '../models/ISample';
import { QuotaAllocationMode, QuotaAllocationModeById } from "../enums/quota-allocation-mode.enum";
import { IS1EventCategory } from "../models/IS1EventCategory";
import { Utils } from '../Utils';
import { IS1FezaUserModel } from '../models/IS1FezaUserModel';


@Injectable()
export class S1DataService {
  s1Token: IS1Token = null;
  s1Url: string;
  private readonly _answerCache = new Map<number, Observable<IS1Answer[]>>();
  private readonly _questionCache = new Map<string, Observable<IS1Question[]>>();
  private readonly _langCache = new Map<string, Observable<ILanguage[]>>();

  constructor(
    private http: HttpClient) {
    this.s1Url = environment.S1Url;
  }

  private S1Call<T>(methodPathForCall: string): Observable<T> {
    return this.http.get<T>(this.s1Url + methodPathForCall);
  }

  getQuestion(countryCode: string, locale: string, questionId: number): Observable<IS1Question> {
    //we need the specific locale, and country default.
    //on QA BE doesn't have a default... so I'm searching all locales for the question id

    //var countryDefaultLocale = this.getDefaultLocale(countryCode).pipe(mergeMap(x => this.getQuestions(x || "en-us")));
    //return Observable.merge(this.getQuestions(locale), countryDefaultLocale).pipe(map(items => items.find(item => item.id === +questionId)));

    return this.getCountryLocales(countryCode)
      .pipe(map(langs => langs.map(z => z.locale)))
      .pipe(mergeMap(loc => loc))
      .pipe(mergeMap(locale => this.getQuestions(locale)))
      .pipe(flatMap(x => x))
      .pipe(filter(x => x.id === +questionId));
  }

  getCountryLocales(countryCode: string): Observable<ILanguage[]> {
    //console.log(`getDefaultLocale : ${countryCode}`);
    return this.getLocales().pipe(map(items => items.filter(item => item.countryCode !="" && item.countryCode === countryCode)));
  }

  getLocales(): Observable<ILanguage[]> {
    if (!this._langCache.get("locales")) {
      this._langCache.set("locales", this.S1Call<ILanguage[]>(`/common/1/locales?countryId=-1`).pipe(shareReplay(1)));
    }
    return this._langCache.get("locales");
  }

  getDefaultLocale(countryCode: string): Observable<string> {
    //console.log(`getDefaultLocale : ${countryCode}`);
    return this.getLocales().pipe(map(items => items.find(item => item.countryCode == countryCode && item.countryDefault))).map(x => x.locale);
  } 

  getCountries(): Observable<ICountry[]> {
    return this.S1Call<ICountry[]>("/common/1/countries");
  }

  getSampleCategories(): Observable<Array<ISampleCategory>> {
    return this.S1Call<Array<ISampleCategory>>("/common/1/sample-category-list");
  }

  getResearchTypes(): Observable<Array<IItem>> {
    return this.S1Call<Array<IItem>>("/common/1/research-types");
  }

  getLanguages(countryId: number): Observable<Array<ILanguage>> {
    return this.S1Call<Array<ILanguage>>("/common/1/locales?countryId=" + countryId)
      .map(x => x.sort((x, y) => (+y.countryDefault) - (+x.countryDefault)));
  }

  getCurrencies(): Observable<ICurrency[]> {
    return this.S1Call<ICurrency[]>("/common/1/currency-list").pipe(map(x => x.map(cur => {
      cur.label = cur.code + " " + cur.symbol + " " + cur.name;
      return cur;
    })));
  }

  getBusinessUnits(): Observable<IItem[]> {
    return this.S1Call<IBillingEntity[]>("/common/1/billing-entities-list")
      .pipe(map(x => x.map(be => new Item(be.supplierInvoicingId, be.billingEntityName))));
  }

  getCategories(): Observable<ICategory[]> {
    return this.S1Call<ICategory[]>('/common/1/categories');
  }

  getQuotaTables(surveyId): Observable<IQuotaTable[]> {
      return this.S1Call<IS1Quota[]>("/feza/Subsample/" + surveyId + "/Quota?quotaLevel=standard")
          .pipe(map(x => {
              //let's keep only one since feza doesn't care about suppliers, make a copy of the array in "tmp" to avoid changing the thing we are iterating on
              x = x.filter((val, indx, arr) => { return arr.map(item => item.quotaId).indexOf(val.quotaId) === indx; });
              x = x.filter(q => (q.questions || []).length > 0);
              let tmp = x.slice();
              let tableNames = new Set<string>();
              for (let q of tmp) tableNames.add(q.tableName);
              let tables: IQuotaTable[] = [];
              tableNames.forEach(tableName => {
                  let table = { tableName: tableName, quotas: [] } as IQuotaTable;
                  for (let s1Quota of tmp) {
                      if (s1Quota.tableName === tableName) {
                          table.quotas.push({
                              completes: {},
                              /*we'd use "s1quota.percent" for "percentage but we want the percent per quotas, not per supplier per quota, so recalc it here*/
                              percentage: s1Quota.requestedInterviews /
                                  tmp.filter(q => q.tableName === s1Quota.tableName).map(q => q.requestedInterviews)
                                      .reduce((a, b) => a + b) *
                                  100.0,
                              interviewType: "completes",
                              questions: s1Quota.questions.map(this.s1Question2Question),
                            quotaName: s1Quota.quotaName,
                            quotaId: Utils.generateUUID()
                          } as IQuota);
                      }
                  }
                  tables.push(table);
              });
              return tables;
          }));
  }

  getQuestions(locale: string): Observable<IS1Question[]> {

    if (!this._questionCache.get(locale)) {
      this._questionCache.set(locale, this.S1Call<IS1Question[]>(`/common/1/locales/${locale}/questions`).pipe(shareReplay(1)));
    }
    return this._questionCache.get(locale);
  } 

  getAnswers(questionId: number): Observable<IS1Answer[]> {
    if (!this._answerCache.get(questionId)) {
      this._answerCache.set(questionId, this.S1Call<IS1Answer[]>(`/common/1/locales/any/questions/${questionId}/answers`).pipe(shareReplay(1)));
    }
    return this._answerCache.get(questionId);
  }

  getUserInfo(userId: number): Observable<IS1UserModel> {
    return this.S1Call<IS1UserModel>(`/admin/UserInfo/${userId}`);
  }

  getFezaUserInfo(): Observable<IS1FezaUserModel> {
    return this.S1Call<IS1FezaUserModel>(`/feza/UserInfo`);
  }

  getBidResponse(id): Observable<IBid> {
    var bid = new Bid();
    bid.bidId = id;
    return Observable.of(bid);
  }

  public getSampleOneUserToken(userName: string, password: string): Observable<IS1Token> {
	  userName = "ipsosgroup\\" + userName.replace("ipsosgroup\\", "");
	  password = encodeURIComponent(password);
    return this.http.post<IS1Token>(this.s1Url + "/token",
      "grant_type=password&username=" + userName + "&password=" + password,
      {
		    headers: new HttpHeaders({ 'UserType': '1', 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
        observe: 'response'
      }).map(resp => {
      var token = resp.body as IS1Token;
      token.userId = parseInt(resp.headers.get("ApplicationUserId"));
      return token;
    });
  }

  getEvent(eventId: number, details:boolean=false): Observable<IS1Event> {
    return this.S1Call<IS1Event>(`/admin/Event/${eventId}?basic=${!details}`);
  }

  public getSubsampleList(event: string): Observable<IS1Event[]>{
    return this.S1Call<IS1Event[]>(`/admin/event/${event}?basic=false`);
  }

  public getSurvey(subsampleId: number): Observable<IS1SubSample> {
    return this.S1Call<IS1SubSample>(`/admin/Subsample/${subsampleId}`);
  }

  public getEventCategories(eventId: number): Observable<IS1EventCategory[]> {
    return this.S1Call<IS1EventCategory[]>(`/admin/Event/${eventId}/Category`);
    }

    public getBidNameFromSubsample(subsampleId: number): string {
        return `S1-Subsample-${subsampleId}`;
    }
    public BidNameFromEventId(eventId:number): string {
       return `S1-Event-${eventId}`;
    }

    public getBidFromEventId(eventId: number, email: string, previousRequest: IBid): Observable<IBid> {
        
        return Observable.forkJoin(this.getCountries(), this.getResearchTypes(), this.getEventCategories(eventId), this.getEvent(eventId, true))
            .pipe(mergeMap(([countries, researchTypes, eventCategories, event]) => {
                let getSubLst = [], getTablesLst = [];
                for (let c of event.countries)
                    for (let s of c.subsamples) {
                        s.countryId = c.id;
                        getSubLst.push(this.getSurvey(s.id));
                        getTablesLst.push(this.getQuotaTables(s.id));
                    }
                return Observable.forkJoin(getSubLst).pipe(mergeMap((subSamples) => {
                    return Observable.forkJoin(getTablesLst).map((quotaTablesLst) => {
                        let bid: IBid = null;
                        for (let subIndex = 0; subIndex < subSamples.length; subIndex++) {
                            try {
                                let subsample: IS1SubSample = subSamples[subIndex] as IS1SubSample;
                                let quotaTables: IQuotaTable[] = ((quotaTablesLst as IQuotaTable[][])[subIndex]);
                                if (bid === null) {
                                    bid = this.createBidFromDataSources(event.name, event, quotaTables, countries, researchTypes, eventCategories, email);
                                    bid.bidId = this.BidNameFromEventId(eventId);
                                }
                                if (subsample.status === "Live" || subsample.status === "Pending" || subsample.status === "Live for testing" || subsample.status === "Testing" || subsample.status === "Pending or testing") {
                                    let feza = this.createSampleFromDataSources(bid, subsample, event, quotaTables, countries, researchTypes, eventCategories, email, previousRequest);
                                    bid.feasibilities.push(feza);
                                }
                            } catch (e) {
                                //skip a bad sub sample on the event
                                debugger;
                            }
                        }
                        return bid;
                    });
                }));
            }));
    }

    

    public getBidFromSubsampleId(subsampleId: number, email: string, oldBid: IBid): Observable<IBid> {
    return this.getSurvey(subsampleId).pipe(mergeMap(survey => {
      return Observable.forkJoin(
          this.getEvent(survey.surveyEventId),
          this.getQuotaTables(subsampleId),
          this.getCountries(),
          this.getResearchTypes(),
          this.getEventCategories(survey.surveyEventId))
        .map((data) => {
          let bid = this.createBidFromDataSources(survey.name, data[0], data[1], data[2], data[3], data[4], email);
            bid.feasibilities.push(this.createSampleFromDataSources(bid, survey, data[0], data[1], data[2], data[3], data[4], email, oldBid));
            bid.bidId = this.getBidNameFromSubsample(subsampleId);
          return bid;
        });
    }));
  }

  private s1Question2Question(s1Question:IS1Question) : IQuestion {
    let answers : IAnswer[] = [];
    for (let ans of s1Question.answers) {
      answers.push({
        id: ans.id,
        rangeEnd: ans.rangeEnd,
        rangeStart: ans.rangeStart,
        listId: ans.listId,
        text: ans.text,
        answerText: ans.text,
        listIdLastUpdatedDate: ans.listIdLastUpdatedDate
      } as IAnswer);
    }
    return { id: s1Question.id, operator: "OR", answers: answers, responseCode:"Ok" } as IQuestion;
  }

    private createBidFromDataSources(
      bidName: string,
      event: IS1Event,
      quotaTables: IQuotaTable[],
      countries: ICountry[],
      researchTypes: IItem[],
      eventCategories: IS1EventCategory[],
      email: string
    ): IBid {
    var bid = new Bid();
    bid.bidReference.client = "S1FezaRedir";
    bid.bidReference.email = email;
    bid.bidName = bidName;
    bid.bidExpirationDate = null;
    bid.bidMode = BidModeList[BidMode.Postcommissioning].name;
    bid.bidCurrency = "EUR";
    bid.testMode = false;
    bid.billingEntity = event.billingEntityName;
    bid.jobNumberExternal = event.externalJobNumber;
    bid.feasibilities = [];
    return bid;
  }

    private createSampleFromDataSources(bid: IBid,
        survey: IS1SubSample,
        event: IS1Event,
        quotaTables: IQuotaTable[],
        countries: ICountry[],
        researchTypes: IItem[],
        eventCategories: IS1EventCategory[],
        email: string,
        previousRequest: IBid
    ): ISample {
        var sample = new Sample();
        sample.feasibilityId = Utils.generateUUID();
        sample.subSampleId = survey.id;
        sample.countryCode = null;
        //get sample country code from country id in S1
        for (var c of countries) {
            if (c.id == survey.countryId) {
                sample.countryCode = c.code;
            }
        }
        if (sample.countryCode == null)
            throw new Error("Country code is not set in sample one!");
        sample.sampleName = `${sample.countryCode} ${survey.name} Cortex:${survey.id}`;
        for (var feza of previousRequest.feasibilities) {
            if (feza.sampleName === sample.sampleName) {
                sample.feasibilityId = feza.feasibilityId;
                //console.log("Resuseing old id!");
            }
        }
        sample.countryCultures = [];
        //get locale list from s1 list of languages
        for (var lang of survey.languages) {
            sample.countryCultures.push(lang.locale);
        }
        sample.flexibleFieldDuration = false;

        var startTime = new Date(Date.parse(survey.startTime));
        var endTime = new Date(Date.parse(survey.endTime));
        if (survey.endTime != null)
            sample.hoursInField = sample._hoursInField = Math.round(Math.abs(endTime.getTime() - startTime.getTime()) / (60 * 60 * 1000));//ticks to hours
        sample.fieldStartDate = startTime;
        sample.lengthOfInterview = Math.round(survey.expectedLOI);
        sample.researchCategories = [];
        for (var cat of eventCategories) {
            sample.researchCategories.push(new ResearchCategory(cat.mainCategory.name, cat.genericCategory.name, cat.category.name));
        }
        sample.sampleCategoryId = survey.sampleCategory;
        sample.sampleSubCategoryId = survey.sampleSubcategory;
        sample.feasibilityCheckEnabled = survey.isApiCompatible;
        //get researchTypeName from researchTypeId
        researchTypes.forEach((item: IItem) => {
            if (item.id == survey.researchTypeId) {
                sample.researchType = item.name;
            }
        });
        if (sample.researchType == null)
            throw new Error("Research type is not set in sample one!");
        sample.allowDesktop = event.allowComputer;
        sample.allowMobile = event.allowMobile;
        sample.allowTablet = event.allowTablet;
        sample.collectPII = survey.collectsPII;
        sample.facialCoding = survey.facialCoding;
        sample.numberOfCompletes = `${survey.targetCompletes}`;
        sample.incidenceRatePercent = survey.expectedIncidence;
        sample.quotaAllocationMode = QuotaAllocationModeById(QuotaAllocationMode.MaxNonBalanced).name;//TODO FIX ME BACK TO QuotaAllocationMode.NonBalanced
        sample.quotaTables = quotaTables;
        return sample;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/s1data.service.ts